/**
 * Personal data category rows for grouped selects (PD inventory, etc.).
 * Reference cache returns parentId as ObjectId string; legacy dropdown populates parentId as object.
 */

export function categoryRowId(cat) {
    return String(cat?._id ?? cat?.id ?? cat?.value ?? '');
}

function parentIdRaw(cat) {
    const raw = cat?.parentId;
    if (raw == null || raw === '' || raw === 'none') return null;
    return raw;
}

function resolveParentMeta(cat, nameById) {
    const raw = parentIdRaw(cat);
    if (!raw) return null;

    if (typeof raw === 'object') {
        const id = String(raw._id ?? raw.id ?? '');
        const name = (raw.name && String(raw.name).trim()) || nameById.get(id) || '';
        return id ? { id, name } : null;
    }

    const id = String(raw);
    return { id, name: nameById.get(id) ?? '' };
}

/**
 * Active rows only; attach populated parentId when parent is a string id.
 */
export function enrichPersonalDataCategoryRows(categories) {
    if (!Array.isArray(categories) || categories.length === 0) return [];

    const rows = categories.filter((c) => !c.status || c.status === 'active');
    const nameById = new Map();
    for (const cat of rows) {
        const id = categoryRowId(cat);
        if (id) {
            nameById.set(id, cat.name || cat.label || '');
        }
    }

    return rows.map((cat) => {
        const parent = resolveParentMeta(cat, nameById);
        if (!parent) return cat;
        return {
            ...cat,
            parentId: { _id: parent.id, name: parent.name },
        };
    });
}

/**
 * Sub-categories grouped under parent name (root categories are excluded).
 * @returns {Record<string, { label: string, options: object[] }>}
 */
export function groupPersonalDataCategoriesForSelect(categories) {
    const enriched = enrichPersonalDataCategoryRows(categories);

    return enriched.reduce((acc, cat) => {
        const parent = cat.parentId;
        if (!parent || typeof parent !== 'object') return acc;

        const pId = String(parent._id ?? parent.id ?? '');
        const pName = parent.name || '';
        if (!pId) return acc;

        if (!acc[pId]) {
            acc[pId] = { label: pName, options: [] };
        }
        acc[pId].options.push(cat);
        return acc;
    }, {});
}

export function hasPersonalDataCategorySelectOptions(grouped) {
    return Object.values(grouped || {}).some((g) => Array.isArray(g.options) && g.options.length > 0);
}
